import { cn } from '@/utils/utils';
import React from 'react';

// TimelineConnector Component
interface TimelineConnectorProps {
    isLast?: boolean,
    className?: string
}

const TimelineConnector = React.forwardRef<HTMLDivElement, TimelineConnectorProps>(
    ({ isLast, className }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(
                    `w-0.5 ${isLast ? 'h-0' : 'h-full min-h-16 border-l-2 border-dashed border-gray-300'}`,
                    className
                )}
            >
            </div>
        );
    }
);

TimelineConnector.displayName = 'TimelineConnector';

// TimelineIcon Component
export interface TimelineIconProps {
    children?: React.ReactNode,
    className?: string
}

const TimelineIcon = React.forwardRef<HTMLDivElement, TimelineIconProps>(
    ({ children, className }, ref) => {
        return (
            <div
                ref={ref}
                className={cn('w-5 h-5 rounded-full', className)}
            >
                {children}
            </div>
        );
    }
);

TimelineIcon.displayName = 'TimelineIcon';

// TimelineContent Component
export interface TimelineContentProps {
    children: React.ReactNode,
    className?: string
}

const TimelineContent = React.forwardRef<HTMLDivElement, TimelineContentProps>(
    ({ children, className }, ref) => {
        return (
            <div ref={ref} className={cn('ml-4', className)}>
                {children}
            </div>
        );
    }
);

TimelineContent.displayName = 'TimelineContent';
// TimelineItem Component
export interface TimelineItemProps {
    icon: { children: React.ReactNode, className?: string, ref?: React.Ref<HTMLDivElement> },
    content: { children: React.ReactNode, className?: string, ref?: React.Ref<HTMLDivElement> },

    isLast: boolean
}

const TimelineItem = React.forwardRef<HTMLDivElement, TimelineItemProps>(
    ({ icon, content, isLast }, ref) => {
        return (
            <div ref={ref} className='flex items-start'>
                <div className='flex flex-col items-center relative'>
                    <TimelineIcon className={icon?.className} ref={icon?.ref}>
                        {icon?.children}
                    </TimelineIcon>
                    <TimelineConnector isLast={isLast} />
                </div>
                <div className='ml-2 flex flex-col justify-start'>
                    <TimelineContent className={content?.className} ref={content?.ref}>
                        {content?.children}
                    </TimelineContent>
                </div>
            </div>
        );
    }
);

TimelineItem.displayName = 'TimelineItem';

// Timeline Component
export interface TimelineProps {
    events: Array<{
        icon: { children: React.ReactNode, className?: string, ref?: React.Ref<HTMLDivElement> },
        content: { children: React.ReactNode, className?: string, ref?: React.Ref<HTMLDivElement> },
        isLast: boolean
    }>
}

const Timeline = React.forwardRef<HTMLDivElement, TimelineProps>(({ events }, ref) => {
    return (
        <div ref={ref} className='relative'>
            {events.map((event, index) => (
                <TimelineItem
                    key={index}
                    icon={event.icon}
                    content={event.content}
                    isLast={index === events.length - 1}
                />
            ))}
        </div>
    );
});

Timeline.displayName = 'Timeline';
export {
    Timeline,
    TimelineConnector,
    TimelineContent,
    TimelineIcon,
    TimelineItem
};
